// NPC Shop tab body — one row per NPC-tradable item, both directions
// (Buy from NPC / Sell to NPC) inline with a shared qty spinner.
// Phase 4 Step 17.
//
// Tab owns its own state (qty per item, per-row pending flags) — a
// deliberate asymmetry from market-listings-tab.ts, where state
// lives on MarketUI. Driver: market-ui.ts is at the §10 400-line
// cap and can't absorb new state + handlers without extraction.
// MarketNpcTab is structurally simpler than the listings flow (no
// inventory selection mode, no centralized form state across tabs),
// so owning state locally is straightforward. __nexusDebug reaches
// in via getters.
//
// Step 19 will call clearBuyPending(itemId) / clearSellPending(itemId)
// from server-response handlers to release the disabled state.

import { Container, Graphics, Text } from "pixi.js";
import type { ItemId } from "../../shared/items.ts";
import { getItemDef, items } from "../../shared/items.ts";
import type { InventorySlot } from "../../shared/messages.ts";
import type { NpcInventory, NpcPriceEntry } from "../../shared/market.ts";
import {
  BTN_H,
  BUTTON_PALETTE_BUY,
  BUTTON_PALETTE_CANCEL,
  makeRowButton,
  renderSpinBtn,
  SPIN_BTN_SZ,
} from "./market-ui-widgets.ts";
import { computeNpcBuyDisable, computeNpcSellDisable } from "./market-npc-gates.ts";

const PAD = 12; // contracts:allow-literal — inner padding
const ROW_H = 36; // contracts:allow-literal — npc row height (taller than listings: more controls)
const ROW_GAP = 4; // contracts:allow-literal
const HEADER_ROW_H = 22; // contracts:allow-literal
const SWATCH_SZ = 18; // contracts:allow-literal
const BTN_W = 72; // contracts:allow-literal — Buy/Sell button width
const QTY_DISPLAY_W = 36; // contracts:allow-literal — fixed width for qty digits
const QTY_MIN = 1; // contracts:allow-literal — floor
const QTY_MAX = 999; // contracts:allow-literal — cap; Fix 2 raised 99→999 so hold-to-accelerate spinners can actually reach big quantities in <5s

const TABLE_BG = 0x0e0e22;
const TABLE_BORDER = 0x223366;
const ROW_BG = 0x1a1a3a;
const ROW_BG_ALT = 0x151530;
const ROW_BORDER = 0x2a2a55;
const TEXT_BRIGHT = 0xeeeeff;
const TEXT_MUTED = 0x8888aa;
const TEXT_PLACEHOLDER = 0x556688;
const BORDER_DEFAULT = 0x555555;
const BODY_SIZE = 11; // contracts:allow-literal

export type NpcTabSnapshot = {
  npcInventory: NpcInventory;
  npcPrices: readonly NpcPriceEntry[];
  playerInventory: readonly InventorySlot[];
  playerCredits: number;
};

export type NpcTabCallbacks = {
  onBuy: (itemId: ItemId, quantity: number) => void;
  onSell: (itemId: ItemId, quantity: number) => void;
};

export class MarketNpcTab {
  private container: Container | null = null;
  // Last build inputs cached so internal state changes (qty, pending)
  // can rebuild without main.ts re-feeding the snapshot.
  private lastParent: Container | null = null;
  private lastRect: { x: number; y: number; w: number; h: number } | null = null;
  private lastSnap: NpcTabSnapshot | null = null;
  private lastCb: NpcTabCallbacks | null = null;

  // Per-item qty + pending. Map / Set so we don't carry stale rows
  // when the catalog evolves (Phase 5+ may add items).
  private qty = new Map<string, number>();
  private buyPending = new Set<string>();
  private sellPending = new Set<string>();

  build(
    parent: Container,
    x: number,
    y: number,
    w: number,
    h: number,
    snap: NpcTabSnapshot,
    cb: NpcTabCallbacks,
  ): void {
    this.lastParent = parent;
    this.lastRect = { x, y, w, h };
    this.lastSnap = snap;
    this.lastCb = cb;
    this.render();
  }

  teardown(): void {
    if (this.container) {
      this.container.parent?.removeChild(this.container);
      this.container.destroy({ children: true });
      this.container = null;
    }
  }

  // Step 19 hooks. Both clear the pending flag and trigger a rebuild
  // so the disabled state lifts.
  clearBuyPending(itemId: string): void {
    if (!this.buyPending.has(itemId)) return;
    this.buyPending.delete(itemId);
    this.rebuildIfMounted();
  }
  clearSellPending(itemId: string): void {
    if (!this.sellPending.has(itemId)) return;
    this.sellPending.delete(itemId);
    this.rebuildIfMounted();
  }

  // __nexusDebug projections — Step 21 browser tests assert these.
  get qtyByItem(): Record<string, number> {
    return Object.fromEntries(this.qty);
  }
  get buyPendingItems(): string[] { return Array.from(this.buyPending); }
  get sellPendingItems(): string[] { return Array.from(this.sellPending); }

  // ------------------------------------------------------------------
  // Internal render
  // ------------------------------------------------------------------

  private rebuildIfMounted(): void {
    if (this.lastParent && this.lastRect && this.lastSnap && this.lastCb) {
      this.render();
    }
  }

  private render(): void {
    if (!this.lastParent || !this.lastRect || !this.lastSnap || !this.lastCb) return;
    const parent = this.lastParent;
    const { x, y, w, h } = this.lastRect;
    const snap = this.lastSnap;
    const cb = this.lastCb;

    this.teardown();
    const root = new Container();
    root.x = x;
    root.y = y;
    parent.addChild(root);
    this.container = root;

    root.addChild(
      new Graphics().rect(0, 0, w, h).fill({ color: TABLE_BG }).stroke({ color: TABLE_BORDER, width: 1 }), // contracts:allow-literal
    );

    root.addChild(this.buildColumnHeaders(w));

    let rowY = HEADER_ROW_H;
    let index = 0;
    if (snap.npcPrices.length === 0) {
      const empty = new Text({
        text: "NPC catalog is empty.",
        style: { fontSize: BODY_SIZE, fill: TEXT_PLACEHOLDER, fontFamily: "monospace", fontStyle: "italic" },
      });
      empty.x = Math.round((w - empty.width) / 2); // contracts:allow-literal — centering idiom
      empty.y = HEADER_ROW_H + 24; // contracts:allow-literal — gap below header
      root.addChild(empty);
      return;
    }
    for (const entry of snap.npcPrices) {
      const row = this.buildRow(w, entry, snap, cb, index % 2 === 1); // contracts:allow-literal — alt shading
      row.y = rowY;
      root.addChild(row);
      rowY += ROW_H + ROW_GAP;
      index++;
    }
  }

  private buildColumnHeaders(w: number): Container {
    const header = new Container();
    header.addChild(new Graphics().rect(0, 0, w, HEADER_ROW_H).fill({ color: ROW_BG_ALT }));
    const cols = columnLayout(w);
    const labels: Array<[keyof typeof cols, string]> = [
      ["item", "Item"],
      ["buyPrice", "Buy"],
      ["sellPrice", "Sell"],
      ["qty", "Qty"],
      ["actions", ""],
    ];
    for (const [key, text] of labels) {
      const t = new Text({
        text,
        style: { fontSize: BODY_SIZE, fill: TEXT_MUTED, fontFamily: "monospace", fontWeight: "bold" },
      });
      t.x = cols[key].x + PAD / 2; // contracts:allow-literal — half-pad
      t.y = Math.round((HEADER_ROW_H - t.height) / 2); // contracts:allow-literal — centering idiom
      header.addChild(t);
    }
    return header;
  }

  private buildRow(
    w: number,
    entry: NpcPriceEntry,
    snap: NpcTabSnapshot,
    cb: NpcTabCallbacks,
    alt: boolean,
  ): Container {
    const row = new Container();
    row.addChild(
      new Graphics()
        .rect(0, 0, w, ROW_H)
        .fill({ color: alt ? ROW_BG_ALT : ROW_BG })
        .stroke({ color: ROW_BORDER, width: 1 }), // contracts:allow-literal
    );

    const cols = columnLayout(w);

    // Item cell — swatch + name
    const itemCell = new Container();
    itemCell.x = cols.item.x + PAD / 2; // contracts:allow-literal
    itemCell.y = Math.round((ROW_H - SWATCH_SZ) / 2); // contracts:allow-literal — centering idiom
    row.addChild(itemCell);
    const itemDef = getItemDef(entry.itemId) ?? items.copper;
    itemCell.addChild(
      new Graphics()
        .roundRect(0, 0, SWATCH_SZ, SWATCH_SZ, 3) // contracts:allow-literal — corner radius
        .fill({ color: itemDef.color })
        .stroke({ color: BORDER_DEFAULT, width: 1 }), // contracts:allow-literal
    );
    const nameText = new Text({
      text: itemDef.name,
      style: { fontSize: BODY_SIZE, fill: TEXT_BRIGHT, fontFamily: "monospace" },
    });
    nameText.x = SWATCH_SZ + 6; // contracts:allow-literal — gap
    nameText.y = Math.round((SWATCH_SZ - nameText.height) / 2); // contracts:allow-literal — centering idiom
    itemCell.addChild(nameText);

    // Prices
    this.placePriceText(row, cols.buyPrice, `\u20B5 ${entry.sellPrice}`); // NPC sells at sellPrice → "Buy" cost
    this.placePriceText(row, cols.sellPrice, `\u20B5 ${entry.buyPrice}`); // NPC buys at buyPrice → "Sell" payout

    // Qty spinner
    const itemId = entry.itemId;
    const qty = this.qty.get(itemId) ?? QTY_MIN;
    const qtyEnabled = !this.buyPending.has(itemId) && !this.sellPending.has(itemId);
    let cx = cols.qty.x + PAD / 2; // contracts:allow-literal
    const spinY = Math.round((ROW_H - SPIN_BTN_SZ) / 2); // contracts:allow-literal — centering idiom
    cx = renderSpinBtn(row, cx, spinY, "−", qtyEnabled && qty > QTY_MIN, (step) => this.adjustQty(itemId, -step)) + 4; // contracts:allow-literal — gap
    const qtyLbl = new Text({
      text: String(qty),
      style: { fontSize: BODY_SIZE, fill: TEXT_BRIGHT, fontFamily: "monospace", fontWeight: "bold" },
    });
    qtyLbl.x = cx + Math.round((QTY_DISPLAY_W - qtyLbl.width) / 2); // contracts:allow-literal — centering idiom
    qtyLbl.y = Math.round((ROW_H - qtyLbl.height) / 2); // contracts:allow-literal — centering idiom
    row.addChild(qtyLbl);
    cx += QTY_DISPLAY_W + 4; // contracts:allow-literal — gap
    renderSpinBtn(row, cx, spinY, "+", qtyEnabled && qty < QTY_MAX, (step) => this.adjustQty(itemId, step));

    // Action buttons
    const buyDisabledReason = this.computeBuyDisable(entry, qty, snap);
    const sellDisabledReason = this.computeSellDisable(entry, qty, snap);
    const buyPending = this.buyPending.has(itemId);
    const sellPending = this.sellPending.has(itemId);

    const actionsX = cols.actions.x + PAD / 2; // contracts:allow-literal
    const buyBtn = makeRowButton({
      label: "Buy",
      palette: BUTTON_PALETTE_BUY,
      disabled: buyPending || buyDisabledReason !== null,
      width: BTN_W,
      onClick: () => this.handleBuy(itemId, qty, cb),
      overrideLabel: buyPending ? "Buying…" : undefined,
    });
    buyBtn.x = actionsX;
    buyBtn.y = Math.round((ROW_H - BTN_H) / 2); // contracts:allow-literal — centering idiom
    row.addChild(buyBtn);

    const sellBtn = makeRowButton({
      label: "Sell",
      palette: BUTTON_PALETTE_CANCEL,
      disabled: sellPending || sellDisabledReason !== null,
      width: BTN_W,
      onClick: () => this.handleSell(itemId, qty, cb),
      overrideLabel: sellPending ? "Selling…" : undefined,
    });
    sellBtn.x = actionsX + BTN_W + 6; // contracts:allow-literal — gap between buttons
    sellBtn.y = Math.round((ROW_H - BTN_H) / 2); // contracts:allow-literal — centering idiom
    row.addChild(sellBtn);

    return row;
  }

  private placePriceText(row: Container, col: { x: number; w: number }, label: string): void {
    const t = new Text({
      text: label,
      style: { fontSize: BODY_SIZE, fill: TEXT_BRIGHT, fontFamily: "monospace" },
    });
    t.x = col.x + PAD / 2; // contracts:allow-literal — half-pad
    t.y = Math.round((ROW_H - t.height) / 2); // contracts:allow-literal — centering idiom
    row.addChild(t);
  }

  private computeBuyDisable(
    entry: NpcPriceEntry,
    qty: number,
    snap: NpcTabSnapshot,
  ): "out_of_stock" | "insufficient_credits" | null {
    return computeNpcBuyDisable(entry, qty, snap);
  }

  private computeSellDisable(
    entry: NpcPriceEntry,
    qty: number,
    snap: NpcTabSnapshot,
  ): "no_inventory" | "npc_saturated" | null {
    return computeNpcSellDisable(entry, qty, snap);
  }

  private adjustQty(itemId: string, delta: number): void {
    const current = this.qty.get(itemId) ?? QTY_MIN;
    const next = Math.max(QTY_MIN, Math.min(QTY_MAX, current + delta));
    if (next === current) return;
    this.qty.set(itemId, next);
    this.rebuildIfMounted();
  }

  private handleBuy(itemId: ItemId, qty: number, cb: NpcTabCallbacks): void {
    if (this.buyPending.has(itemId)) return;
    this.buyPending.add(itemId);
    cb.onBuy(itemId, qty);
    this.rebuildIfMounted();
  }

  private handleSell(itemId: ItemId, qty: number, cb: NpcTabCallbacks): void {
    if (this.sellPending.has(itemId)) return;
    this.sellPending.add(itemId);
    cb.onSell(itemId, qty);
    this.rebuildIfMounted();
  }
}

function columnLayout(w: number): {
  item: { x: number; w: number };
  buyPrice: { x: number; w: number };
  sellPrice: { x: number; w: number };
  qty: { x: number; w: number };
  actions: { x: number; w: number };
} {
  // Flex split: item 30%, buyPrice 12%, sellPrice 12%, qty 16%, actions 30%.
  const itemW = Math.round(w * 0.30); // contracts:allow-literal — column ratio
  const buyW = Math.round(w * 0.12); // contracts:allow-literal — column ratio
  const sellW = Math.round(w * 0.12); // contracts:allow-literal — column ratio
  const qtyW = Math.round(w * 0.16); // contracts:allow-literal — column ratio
  const actionsW = w - itemW - buyW - sellW - qtyW;
  return {
    item: { x: 0, w: itemW },
    buyPrice: { x: itemW, w: buyW },
    sellPrice: { x: itemW + buyW, w: sellW },
    qty: { x: itemW + buyW + sellW, w: qtyW },
    actions: { x: itemW + buyW + sellW + qtyW, w: actionsW },
  };
}
